"use client";

import { useMemo, useState, useTransition } from "react";
import { Link } from "@/i18n/navigation";
import { Avatar, Badge, Card, Input, Select } from "@/components/ui";
import { IconPin, IconStar } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import type { Locale, VerificationLevel, ZoneId } from "@/lib/mock";

/** Ícono local (no está en el set compartido de @/components/ui/icons). */
function IconSearch({ size = 22 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.2-3.2" />
    </svg>
  );
}

export interface DiscoverCard {
  slug: string;
  initials: string;
  displayName: string;
  rating: number;
  reviewCount: number;
  verificationLevel: VerificationLevel;
  verificationLabel: string;
  bio: string;
  zoneIds: ZoneId[];
  zoneLabels: string[];
  priceLabel: string;
  serviceNames: string[];
}

export function DiscoverExplorer({
  cards: initialCards,
  zoneOptions,
  serviceOptions,
  locale,
  searchAction,
  labels,
}: {
  cards: DiscoverCard[];
  zoneOptions: { value: ZoneId; label: string }[];
  serviceOptions: string[];
  /** Locale actual — viaja al server action de búsqueda. */
  locale: Locale;
  /** Server action: al cambiar la zona, re-busca en el servidor (dominio real). */
  searchAction: (input: { locale: Locale; zoneId: string }) => Promise<DiscoverCard[]>;
  labels: {
    searchPlaceholder: string;
    zoneLabel: string;
    allZones: string;
    serviceLabel: string;
    allServices: string;
    resultsCountOne: string;
    resultsCountOther: string;
    noResultsTitle: string;
    noResultsHint: string;
    clear: string;
    reviewsTemplate: string;
    viewProfile: string;
    fromLabel: string;
  };
}) {
  const [query, setQuery] = useState("");
  const [zone, setZone] = useState<string>("all");
  const [service, setService] = useState<string>("all");
  // Las tarjetas vienen del servidor; al cambiar la zona se re-piden al server
  // action (búsqueda con el dominio real), no se filtran sobre un array local.
  const [cards, setCards] = useState<DiscoverCard[]>(initialCards);
  const [isPending, startTransition] = useTransition();

  function onZoneChange(nextZone: string) {
    setZone(nextZone);
    startTransition(async () => {
      setCards(await searchAction({ locale, zoneId: nextZone }));
    });
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cards.filter((c) => {
      if (zone !== "all" && !c.zoneIds.includes(zone as ZoneId)) return false;
      if (service !== "all" && !c.serviceNames.includes(service)) return false;
      if (q) {
        const haystack = [c.displayName, c.bio, ...c.serviceNames]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [cards, query, zone, service]);

  const hasFilters = query.trim() !== "" || zone !== "all" || service !== "all";

  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
        <Input
          leading={<IconSearch size={17} />}
          placeholder={labels.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={labels.searchPlaceholder}
        />
        <Select
          aria-label={labels.zoneLabel}
          value={zone}
          onChange={(e) => onZoneChange(e.target.value)}
          options={[
            { value: "all", label: labels.allZones },
            ...zoneOptions.map((z) => ({ value: z.value, label: z.label })),
          ]}
          className="sm:w-48"
        />
        <Select
          aria-label={labels.serviceLabel}
          value={service}
          onChange={(e) => setService(e.target.value)}
          options={[
            { value: "all", label: labels.allServices },
            ...serviceOptions.map((s) => ({ value: s, label: s })),
          ]}
          className="sm:w-56"
        />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-ink-muted">
          {filtered.length === 1
            ? labels.resultsCountOne
            : labels.resultsCountOther.replace("{count}", String(filtered.length))}
        </p>
        {hasFilters && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setService("all");
              onZoneChange("all");
            }}
            className="text-sm font-medium text-forest hover:underline"
          >
            {labels.clear}
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-2 rounded-[var(--radius-app)] border border-dashed border-sand-300 bg-bone-50 px-6 py-14 text-center">
          <p className="font-display text-lg text-ink">{labels.noResultsTitle}</p>
          <p className="text-sm text-ink-muted">{labels.noResultsHint}</p>
        </div>
      ) : (
        <div
          className={cn(
            "mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 transition-opacity",
            isPending && "opacity-60",
          )}
        >
          {filtered.map((c) => (
            <Card key={c.slug} elevation="raised" className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <Avatar initials={c.initials} name={c.displayName} size="lg" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-lg text-ink">{c.displayName}</h3>
                  <div className="mt-0.5 flex items-center gap-1.5 text-sm text-ink-muted">
                    <IconStar size={15} className="text-clay" />
                    <span className="nums font-medium text-ink-soft">
                      {c.rating.toFixed(1)}
                    </span>
                    <span>
                      · {labels.reviewsTemplate.replace("{count}", String(c.reviewCount))}
                    </span>
                  </div>
                  <div className="mt-1">
                    <Badge
                      variant={c.verificationLevel === "L2" ? "forest" : "clay"}
                      dot
                    >
                      {c.verificationLabel}
                    </Badge>
                  </div>
                </div>
              </div>

              <p className="line-clamp-3 text-sm text-ink-soft">{c.bio}</p>

              <div className="mt-auto flex items-center justify-between border-t border-sand-200 pt-3">
                <div
                  className={cn(
                    "flex flex-wrap items-center gap-1.5 text-xs text-ink-muted",
                  )}
                >
                  <IconPin size={14} />
                  {c.zoneLabels.join(" · ")}
                </div>
                <div className="text-right">
                  <span className="text-xs text-ink-muted">{labels.fromLabel} </span>
                  <span className="nums font-display text-base text-ink">
                    {c.priceLabel}
                  </span>
                </div>
              </div>

              <Link
                href={`/app/t/${c.slug}`}
                data-testid="discover-view-profile"
                data-slug={c.slug}
                className="inline-flex h-10 items-center justify-center rounded-full bg-forest text-sm font-medium text-bone-50 transition-colors hover:bg-forest-strong"
              >
                {labels.viewProfile}
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
